import { fmt, fmtDate, fmtQty } from '@k2pos/core';
import { colors, fonts } from '@/theme/tokens';

export interface BillLine {
  name: string;
  unit: string;
  qty: number;
  amount: number;
}

export interface BillImageInput {
  shopName: string;
  phone?: string;
  address?: string;
  invoiceNo: string;
  /** ISO-8601. */
  at: string;
  customerName?: string;
  credit?: boolean;
  lines: BillLine[];
  total: number;
  bank?: string;
  /** The shop's QR as a data URL, from `useShopQr`. */
  qr?: string | null;
  footer?: string;
}

/** Receipt width in CSS pixels; doubled on the canvas so it stays sharp. */
const WIDTH = 380;
const SCALE = 2;
const PAD = 22;

/**
 * Draws the bill onto a canvas rather than screenshotting the DOM.
 *
 * The obvious route is html2canvas or an SVG `foreignObject`, and both fall
 * down on the one thing this bill cannot get wrong: Lao. `foreignObject` loads
 * no external font, so the woff2 would have to be inlined and the marks land
 * wherever the fallback puts them; html2canvas is a second rendering engine
 * with its own opinions about the same. Canvas `fillText` uses the faces the
 * document has already loaded and lets the browser shape the text, which is
 * the same shaper that drew the screen.
 *
 * Laid out twice: once against a throwaway canvas to learn how tall the bill
 * is, then for real. Cheaper than guessing a height and cropping, and the two
 * passes cannot drift because they are the same function.
 */
export async function renderBillImage(input: BillImageInput): Promise<Blob> {
  // Without this the first save after a cold start draws in the fallback font —
  // which is exactly the Lao the app loads its own faces to avoid.
  await document.fonts.ready;
  const qr = input.qr ? await loadImage(input.qr) : null;

  const height = paint(measuringContext(), input, qr);

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH * SCALE;
  canvas.height = Math.ceil(height) * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, WIDTH, height);
  paint(ctx, input, qr);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      'image/png',
    );
  });
}

function measuringContext(): CanvasRenderingContext2D {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  return ctx;
}

/** Lays the bill out top to bottom and returns the height it needed. */
function paint(
  ctx: CanvasRenderingContext2D,
  input: BillImageInput,
  qr: HTMLImageElement | null,
): number {
  const right = WIDTH - PAD;
  let y = PAD + 4;

  const lao = (size: number, weight = 400) => `${weight} ${size}px ${fonts.lao}`;
  const mono = (size: number, weight = 400) => `${weight} ${size}px ${fonts.mono}`;

  const write = (text: string, x: number, font: string, colour: string, align: CanvasTextAlign) => {
    ctx.font = font;
    ctx.fillStyle = colour;
    ctx.textAlign = align;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, x, y);
  };

  // --- the shop ---------------------------------------------------------- //
  y += 20;
  write(input.shopName, WIDTH / 2, lao(20, 800), colors.navy, 'center');
  for (const line of [input.phone, input.address].filter(Boolean) as string[]) {
    y += 15;
    write(line, WIDTH / 2, lao(10.5), colors.textMuted, 'center');
  }

  // --- the bill's own head ----------------------------------------------- //
  y += 20;
  write(`#${input.invoiceNo}`, PAD, mono(11, 700), colors.textMuted, 'left');
  write(fmtDate(new Date(input.at)), right, mono(11), colors.textMuted, 'right');

  if (input.customerName) {
    y += 16;
    write(input.customerName, PAD, lao(11), colors.textMuted, 'left');
    write(input.credit ? 'ຕິດໜີ້' : 'ຈ່າຍເລີຍ', right, lao(11, 700), colors.navy, 'right');
  }

  y += 10;
  rule(ctx, y, 'dashed');

  // --- what was sold ----------------------------------------------------- //
  for (const line of input.lines) {
    y += 20;
    // The amount is drawn first so the name can be wrapped against the space
    // that is actually left: a long Lao product name otherwise runs straight
    // under the figure it belongs to.
    ctx.font = mono(12);
    const amount = fmt(line.amount);
    const amountWidth = ctx.measureText(amount).width;
    write(amount, right, mono(12), colors.text, 'right');

    ctx.font = lao(12);
    const wrapped = wrap(ctx, line.name, WIDTH - PAD * 2 - amountWidth - 12);
    for (const [i, part] of wrapped.entries()) {
      if (i > 0) y += 15;
      write(part, PAD, lao(12), colors.text, 'left');
    }

    y += 13;
    write(`${fmtQty(line.qty)} ${line.unit}`, PAD, mono(10), colors.textFaint, 'left');
  }

  // --- the total --------------------------------------------------------- //
  y += 14;
  rule(ctx, y, 'solid');
  y += 22;
  write('ລວມ', PAD, lao(14, 800), colors.text, 'left');
  write(`${fmt(input.total)} ₭`, right, mono(16, 700), colors.text, 'right');

  // --- how to pay -------------------------------------------------------- //
  if (qr || input.bank) {
    y += 22;
    write('ໂອນເຂົ້າບັນຊີ', WIDTH / 2, lao(10, 700), colors.textMuted, 'center');
    if (qr) {
      const size = 128;
      // Drawn square from its own aspect ratio, never stretched: a squashed QR
      // is a QR that does not scan.
      const scale = Math.min(size / qr.width, size / qr.height);
      const w = qr.width * scale;
      const h = qr.height * scale;
      ctx.drawImage(qr, (WIDTH - w) / 2, y + 6, w, h);
      y += h + 6;
    }
    if (input.bank) {
      y += 16;
      write(input.bank, WIDTH / 2, mono(11), colors.text, 'center');
    }
  }

  if (input.footer) {
    y += 22;
    for (const part of wrapLao(ctx, input.footer, WIDTH - PAD * 2, lao(10.5))) {
      write(part, WIDTH / 2, lao(10.5), colors.textFaint, 'center');
      y += 14;
    }
    y -= 14;
  }

  return y + PAD;
}

function rule(ctx: CanvasRenderingContext2D, y: number, kind: 'dashed' | 'solid'): void {
  ctx.save();
  ctx.strokeStyle = kind === 'solid' ? colors.navy : colors.border;
  ctx.lineWidth = kind === 'solid' ? 1.5 : 1;
  ctx.setLineDash(kind === 'dashed' ? [3, 3] : []);
  ctx.beginPath();
  ctx.moveTo(PAD, y);
  ctx.lineTo(WIDTH - PAD, y);
  ctx.stroke();
  ctx.restore();
}

/**
 * Wraps at spaces, and falls back to breaking mid-word when there are none.
 *
 * Lao does not space its words, so a product name can be one unbreakable token
 * as far as `split(' ')` is concerned. Breaking by character is not correct
 * Lao line-breaking, but it keeps the name on the bill; letting it run off the
 * edge does not.
 */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let current = '';

  for (const word of text.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
    while (ctx.measureText(current).width > maxWidth && current.length > 1) {
      let cut = current.length;
      while (cut > 1 && ctx.measureText(current.slice(0, cut)).width > maxWidth) cut -= 1;
      lines.push(current.slice(0, cut));
      current = current.slice(cut);
    }
  }

  if (current) lines.push(current);
  // Two lines is a bill; five is a paragraph nobody reads off a receipt.
  return lines.slice(0, 2);
}

/** Same wrapping, with the font set first and no line cap. */
function wrapLao(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string,
): string[] {
  ctx.font = font;
  const lines: string[] = [];
  let current = '';
  for (const word of text.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) current = candidate;
    else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('could not decode the shop QR'));
    image.src = src;
  });
}
